import React, { useCallback, useState } from "react";
import { Handle, Position, NodeProps } from "reactflow";
import { X, AlertCircle, Settings } from "lucide-react";
import { ProcessorNodeData } from "../../types/pipeline";
import { PROCESSOR_COLORS } from "../../types/processor";
import { usePipelineBuilderStore } from "../../store/pipelineBuilderStore";
import { useUIStore } from "../../store";
import { isNodeInExecutionFlow } from "../../utils/executionFlow";
import { QuickEditDialog } from "./QuickEditDialog";

// Check if processor supports dynamic inputs
function supportsDynamicInputs(data: ProcessorNodeData): boolean {
  return (
    data.config?.supports_dynamic_inputs === true ||
    ['oneshot_document', 'agentic_task', 'orchestrator', 'vector', 'shell'].includes(data.processor_name)
  );
}

export const ProcessorNode: React.FC<NodeProps<ProcessorNodeData>> = ({
  id,
  data,
  selected,
}) => {
  const removeNode = usePipelineBuilderStore((state) => state.removeNode);
  const selectNode = usePipelineBuilderStore((state) => state.selectNode);
  const edges = usePipelineBuilderStore((state) => state.edges);
  const nodes = usePipelineBuilderStore((state) => state.nodes);
  const theme = useUIStore((state) => state.theme);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showQuickEdit, setShowQuickEdit] = useState(false);

  // Normalize processor_type to uppercase (backend returns lowercase)
  const processorType = (data.processor_type?.toUpperCase() || "SCRIPTED") as
    | "SCRIPTED"
    | "AGENTIC"
    | "UTILITY";
  const colors = PROCESSOR_COLORS[processorType];
  const hasErrors = data.validation_errors && data.validation_errors.length > 0;
  const hasWarnings =
    data.validation_warnings && data.validation_warnings.length > 0;

  // Check if node is connected to execution flow (for disabling data handles per Q1)
  const isInExecutionFlow = isNodeInExecutionFlow(id, edges, nodes);

  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      removeNode(id);
      setShowDeleteConfirm(false);
    },
    [id, removeNode]
  );

  const handleCancelDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(false);
  }, []);

  const handleSettingsClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      selectNode(id);
    },
    [id, selectNode]
  );

  const handleNodeClick = useCallback((e: React.MouseEvent) => {
    // Don't open quick edit if clicking on buttons or handles
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest(".react-flow__handle")) {
      return;
    }
    e.stopPropagation(); // Prevent node selection
    setShowQuickEdit(true);
  }, []);

  const handleCloseQuickEdit = useCallback(async () => {
    setShowQuickEdit(false);

    // Trigger validation if pipeline was previously validated
    const state = usePipelineBuilderStore.getState();
    if (state.validatedAt) {
      try {
        const { buildPipelineSteps } = require("../../utils/executionFlow");
        const { apiService } = require("../../services/api");
        const steps = buildPipelineSteps(state.nodes, state.edges);
        const results = await apiService.validatePipeline({ steps });
        usePipelineBuilderStore.getState().setValidationResults(results);
      } catch (error) {
        console.error(
          "Auto-revalidation after closing quick edit failed:",
          error
        );
      }
    }
  }, []);

  return (
    <>
      <div
        className={`
          ${colors.bg}
          border-2 
          ${
            hasErrors
              ? "border-red-500 shadow-lg shadow-red-200"
              : hasWarnings
              ? "border-yellow-500 shadow-lg shadow-yellow-200"
              : selected
              ? "border-blue-500 shadow-lg"
              : `${colors.border} border-opacity-60`
          }
          rounded-lg w-[280px] shadow-md
          transition-all duration-200
          cursor-pointer
        `}
        onClick={handleNodeClick}
        data-testid="processor-node"
      >
        {/* Header with execution flow handles */}
        <div
          className={`${colors.badge} px-3 py-2 rounded-t-md flex items-center justify-between relative`}
        >
          {/* Execution flow handle (previous_node) - left side, diamond shape */}
          <Handle
            type="target"
            position={Position.Left}
            id="previous_node"
            className="execution-handle !w-3 !h-3 !border-2 !border-slate-500 dark:!border-gray-300"
            style={{
              background: theme === "dark" ? "#e5e5e5" : "#64748b",
              borderRadius: "2px",
              left: "-6px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
            }}
            data-testid="previous-node-handle"
          />

          <button
            onClick={handleSettingsClick}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            title="Open detailed settings"
          >
            <Settings className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 flex-1">
            <span className="font-semibold text-sm truncate">
              {data.name || data.processor_name}
            </span>
            {hasErrors && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                <AlertCircle className="w-3 h-3" />
                {data.validation_errors!.length}
              </span>
            )}
            {!hasErrors && hasWarnings && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full">
                <AlertCircle className="w-3 h-3" />
                {data.validation_warnings!.length}
              </span>
            )}
          </div>
          {!data.isLocked && (
            <button
              onClick={handleDeleteClick}
              className="p-1 hover:bg-red-500 hover:text-white rounded transition-colors ml-2"
              data-testid="delete-button"
              title="Delete node"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {/* Execution flow handle (next_node) - right side, diamond shape */}
          <Handle
            type="source"
            position={Position.Right}
            id="next_node"
            className="execution-handle !w-3 !h-3 !border-2 !border-slate-500 dark:!border-gray-300"
            style={{
              background: theme === "dark" ? "#e5e5e5" : "#64748b",
              borderRadius: "2px",
              right: "-6px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
            }}
            data-testid="next-node-handle"
          />
        </div>

        {/* Order badge - top-right corner */}
        {data.order !== undefined && (
          <div
            className="absolute -top-2 -right-2 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold z-20 shadow-md"
            data-testid="order-badge"
            title={`Execution order: ${data.order}`}
          >
            {data.order}
          </div>
        )}

        {/* Body */}
        <div className="bg-white dark:bg-gray-800 p-3 rounded-b-md space-y-3">
          {/* Step ID */}
          <div
            className="text-xs text-gray-500 dark:text-gray-400 truncate"
            title={`ID: ${data.step_id}`}
          >
            ID: {data.step_id}
          </div>

          {/* Inputs Section */}
          {(supportsDynamicInputs(data) ||
            data.required_input_keys.length > 0 ||
            data.optional_input_keys.length > 0) && (
            <div className="space-y-1">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                INPUTS:
              </div>

              {/* Required Inputs (always shown) */}
              {data.required_input_keys.map((key, index) => (
                <div
                  key={`input-req-${key}`}
                  className="flex items-center gap-2 text-xs"
                  title={
                    !isInExecutionFlow
                      ? "Connect to execution flow first"
                      : undefined
                  }
                >
                  <Handle
                    type="target"
                    position={Position.Left}
                    id={`input-${key}`}
                    className={`!w-3 !h-3 !border-2 relative ${
                      isInExecutionFlow
                        ? "!bg-blue-500 !border-blue-600 !cursor-crosshair"
                        : "!bg-gray-400 dark:!bg-gray-500 !border-gray-500 dark:!border-gray-600 !cursor-not-allowed opacity-50"
                    }`}
                    style={{
                      position: "relative",
                      left: "-12px",
                      top: "auto",
                      transform: "none",
                      pointerEvents: isInExecutionFlow ? "all" : "none",
                    }}
                  />
                  <span
                    className={`font-medium ${
                      isInExecutionFlow
                        ? "text-gray-700 dark:text-gray-300"
                        : "text-gray-400 dark:text-gray-500"
                    }`}
                  >
                    {key} <span className="text-red-500">*</span>
                    {!isInExecutionFlow && (
                      <span className="text-xs ml-1 text-gray-400">
                        (connect flow first)
                      </span>
                    )}
                  </span>
                </div>
              ))}

              {/* Optional Inputs (always shown) */}
              {data.optional_input_keys.map((key, index) => (
                <div
                  key={`input-opt-${key}`}
                  className="flex items-center gap-2 text-xs"
                  title={
                    !isInExecutionFlow
                      ? "Connect to execution flow first"
                      : undefined
                  }
                >
                  <Handle
                    type="target"
                    position={Position.Left}
                    id={`input-${key}`}
                    className={`!w-3 !h-3 !border-2 !border-dashed relative ${
                      isInExecutionFlow
                        ? "!bg-gray-400 !border-gray-500 !cursor-crosshair"
                        : "!bg-gray-400 dark:!bg-gray-500 !border-gray-500 dark:!border-gray-600 !cursor-not-allowed opacity-50"
                    }`}
                    style={{
                      position: "relative",
                      left: "-12px",
                      top: "auto",
                      transform: "none",
                      pointerEvents: isInExecutionFlow ? "all" : "none",
                    }}
                  />
                  <span
                    className={`${
                      isInExecutionFlow
                        ? "text-gray-500 dark:text-gray-400"
                        : "text-gray-400 dark:text-gray-500"
                    }`}
                  >
                    {key}
                    {!isInExecutionFlow && (
                      <span className="text-xs ml-1 text-gray-400">
                        (connect flow first)
                      </span>
                    )}
                  </span>
                </div>
              ))}

              {/* Dynamic Input Support: Show "prompt inputs" handle */}
              {supportsDynamicInputs(data) && (
                <div
                  className="flex items-center gap-2 text-xs"
                  title="Connect to add a custom input"
                >
                  <Handle
                    type="target"
                    position={Position.Left}
                    id="add-input"
                    className={`!w-3 !h-3 !border-2 relative ${
                      isInExecutionFlow
                        ? "!bg-purple-400 !border-purple-600 !cursor-crosshair"
                        : "!bg-gray-400 dark:!bg-gray-500 !border-gray-500 dark:!border-gray-600 !cursor-not-allowed opacity-50"
                    }`}
                    style={{
                      position: "relative",
                      left: "-12px",
                      top: "auto",
                      transform: "none",
                      pointerEvents: isInExecutionFlow ? "all" : "none",
                      clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)", // Diamond shape
                    }}
                  />
                  <span
                    className={`font-medium ${
                      isInExecutionFlow
                        ? "text-purple-600 dark:text-purple-400"
                        : "text-gray-400 dark:text-gray-500"
                    }`}
                  >
                    ➕ prompt inputs
                    {!isInExecutionFlow && (
                      <span className="text-xs ml-1 text-gray-400">
                        (connect flow first)
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Custom Inputs (for dynamic input processors) */}
              {data.custom_inputs?.map((customInput) => {
                // Get type information for this input from input_artifact_keys
                const inputConfig = data.input_artifact_keys?.[customInput.name];
                const inputType = inputConfig?.type || 'string';
                const {
                  getArtifactTypeColor,
                  getArtifactTypeLabel
                } = require('../../utils/artifactTypes');
                const typeColor = getArtifactTypeColor(inputType);
                const typeLabel = getArtifactTypeLabel(inputType);
                
                return (
                  <div
                    key={`input-custom-${customInput.name}`}
                    className="flex items-center gap-2 text-xs"
                    title={
                      customInput.description ||
                      `Custom input: ${customInput.name}`
                    }
                  >
                    <Handle
                      type="target"
                      position={Position.Left}
                      id={`input-${customInput.name}`}
                      className={`!w-3 !h-3 !border-2 relative ${
                        isInExecutionFlow
                          ? "!bg-blue-500 !border-blue-600 !cursor-crosshair"
                          : "!bg-gray-400 dark:!bg-gray-500 !border-gray-500 dark:!border-gray-600 !cursor-not-allowed opacity-50"
                      }`}
                      style={{
                        position: "relative",
                        left: "-12px",
                        top: "auto",
                        transform: "none",
                        pointerEvents: isInExecutionFlow ? "all" : "none",
                      }}
                    />
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span
                        className={`font-medium ${
                          isInExecutionFlow
                            ? "text-gray-700 dark:text-gray-300"
                            : "text-gray-400 dark:text-gray-500"
                        }`}
                      >
                        {customInput.name} <span className="text-red-500">*</span>
                        {!isInExecutionFlow && (
                          <span className="text-xs ml-1 text-gray-400">
                            (connect flow first)
                          </span>
                        )}
                      </span>
                      {/* Type badge - only show if connected */}
                      {inputConfig && (
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                          style={{
                            backgroundColor: `${typeColor}20`,
                            color: typeColor,
                            border: `1px solid ${typeColor}40`,
                          }}
                        >
                          {typeLabel}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Outputs Section */}
          {(() => {
            // Filter outputs before rendering
            const filteredOutputKeys = data.output_keys.filter((key) => {
              // For OneShot processors with dynamic outputs, hide the standard "output" key
              // since the dynamic output replaces it
              const isOneShotProcessor = data.processor_name === 'oneshot_document';
              const hasDynamicOutputs = data.dynamic_outputs && data.dynamic_outputs.length > 0;
              if (isOneShotProcessor && hasDynamicOutputs && key === 'output') {
                return false; // Hide standard "output"
              }
              return true;
            });
            
            // Only show section if there are outputs after filtering
            return filteredOutputKeys.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  OUTPUTS:
                </div>
                {filteredOutputKeys.map((key, index) => {
                // Get type information for this output
                const outputType = data.output_types?.[key] || 'string';
                const {
                  getArtifactTypeColor,
                  getArtifactTypeLabel
                } = require('../../utils/artifactTypes');
                const typeColor = getArtifactTypeColor(outputType);
                const typeLabel = getArtifactTypeLabel(outputType);
                
                return (
                  <div
                    key={`output-${key}`}
                    className="flex items-center justify-between text-xs gap-2"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">
                        {key}
                      </span>
                      {/* Type badge */}
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                        style={{
                          backgroundColor: `${typeColor}20`,
                          color: typeColor,
                          border: `1px solid ${typeColor}40`,
                        }}
                      >
                        {typeLabel}
                      </span>
                    </div>
                    <Handle
                      type="source"
                      position={Position.Right}
                      id={`output-${key}`}
                      className="!w-3 !h-3 !border-2 relative"
                      style={{
                        position: "relative",
                        right: "-12px",
                        top: "auto",
                        transform: "none",
                        backgroundColor: typeColor,
                        borderColor: typeColor,
                      }}
                      data-output-type={outputType}
                    />
                  </div>
                );
              })}
              </div>
            );
          })()}

          {/* Dynamic Outputs Section (from prompts) */}
          {data.dynamic_outputs && data.dynamic_outputs.length > 0 && (
            <div className="space-y-1 mt-2">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                DYNAMIC OUTPUTS:
              </div>
              {data.dynamic_outputs.map((output) => {
                const {
                  getArtifactTypeColor,
                  getArtifactTypeLabel,
                  getDynamicOutputBadgeClass
                } = require('../../utils/artifactTypes');
                const { getDynamicOutputBorderColor } = require('../../utils/dynamicOutputs');
                const typeColor = getArtifactTypeColor(output.type);
                const typeLabel = getArtifactTypeLabel(output.type);
                const dynamicBadgeClass = getDynamicOutputBadgeClass();
                const dynamicBorderColor = getDynamicOutputBorderColor();
                
                return (
                  <div
                    key={`dynamic-output-${output.name}`}
                    className="flex items-center justify-between text-xs gap-2"
                  >
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">
                        {output.name}
                      </span>
                      {/* Type badge */}
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                        style={{
                          backgroundColor: `${typeColor}20`,
                          color: typeColor,
                          border: `1px solid ${typeColor}40`,
                        }}
                      >
                        {typeLabel}
                      </span>
                      {/* Dynamic indicator */}
                      <span className={`text-[10px] font-semibold ${dynamicBadgeClass}`}>
                        ⚡ dynamic
                      </span>
                    </div>
                    <Handle
                      type="source"
                      position={Position.Right}
                      id={`output-${output.name}`}
                      className={`!w-3 !h-3 !border-2 relative ${dynamicBorderColor}`}
                      style={{
                        position: "relative",
                        right: "-12px",
                        top: "auto",
                        transform: "none",
                        backgroundColor: typeColor,
                      }}
                      data-output-type={output.type}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Validation Errors */}
          {hasErrors && (
            <div
              className="nodrag nopan pt-2 border-t border-red-200 dark:border-red-800 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded px-2 -mx-2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowQuickEdit(true);
              }}
              title="Click to edit and fix errors"
            >
              <div className="text-xs font-semibold text-red-600 dark:text-red-400 mb-1">
                ERRORS:
              </div>
              {data.validation_errors!.map((error, index) => (
                <div
                  key={index}
                  className="text-xs text-red-600 dark:text-red-400 break-words"
                >
                  • {error}
                </div>
              ))}
            </div>
          )}

          {/* Validation Warnings */}
          {hasWarnings && (
            <div
              className="nodrag nopan pt-2 border-t border-yellow-200 dark:border-yellow-800 cursor-pointer hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors rounded px-2 -mx-2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowQuickEdit(true);
              }}
              title="Click to edit and fix warnings"
            >
              <div className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 mb-1">
                WARNINGS:
              </div>
              {data.validation_warnings!.map((warning, index) => (
                <div
                  key={index}
                  className="text-xs text-yellow-600 dark:text-yellow-400 break-words"
                >
                  • {warning}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCancelDelete}
          data-testid="delete-confirm-modal"
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-yellow-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Delete Node?
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete{" "}
              <strong>{data.name || data.processor_name}</strong>? This will
              also remove all connections to this node.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                data-testid="cancel-delete-button"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                data-testid="confirm-delete-button"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Edit Dialog */}
      {showQuickEdit && (
        <QuickEditDialog nodeId={id} onClose={handleCloseQuickEdit} />
      )}
    </>
  );
};
